import { memo, useEffect, useRef, useState } from "react"

// ** Third Party Components
import { HelpCircle } from "lucide-react"
import { Play, Stop, UserAdd } from "iconsax-reactjs"
import { useMutation, useQuery } from "@tanstack/react-query"

// ** Store
import { useAuth } from "@pma/stores/auth-store"

// ** Service
import { getActiveTaskSessionsService } from "@pma/services/task"

// ** Helpers
import { showTimerInFormat } from "@pma/utils/helpers"

// ** Utils
import { queryClient } from "@pma/utils/constants"
import { addTaskSession, finishTaskSession } from "@pma/utils/utils"

// ** Custom Hooks
import { toast } from "@pma/hooks/use-toast"
import useTranslator from "@pma/hooks/use-translator"
import { usePermissions } from "@pma/hooks/use-permissions"

const NavbarBottom = () => {
  const { authConfig } = useAuth()
  const { translate } = useTranslator()
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const { isOrgAdmin: isAdmin, organization_id } = usePermissions()

  const userId = authConfig?.id

  const [isRunning, setIsRunning] = useState(false)
  const [elapsedTime, setElapsedTime] = useState(0)

  const { data, refetch } = useQuery({
    queryKey: ["get-active-task-sessions", organization_id],
    queryFn: () => getActiveTaskSessionsService({ organization_id }),
    enabled: !!organization_id,
  })

  const activeSession = data?._active_session
  const taskId = data?.task_id || data?._tasks?.id

  // Initialize timer from active session data
  useEffect(() => {
    if (activeSession && activeSession.in_progress) {
      const startTime = activeSession.start
      const elapsed = Math.floor((Date.now() - startTime) / 1000)
      setElapsedTime(elapsed)
      setIsRunning(true)
    } else {
      setIsRunning(false)
      setElapsedTime(0)
    }
  }, [activeSession])

  // Timer tick
  useEffect(() => {
    if (isRunning) {
      timerRef.current = setInterval(() => {
        setElapsedTime((prev) => prev + 1)
      }, 1000)
    } else {
      if (timerRef.current) clearInterval(timerRef.current)
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }, [isRunning])

  const addSessionMutation = useMutation({
    mutationKey: ["add-task-session-navbar"],
    mutationFn: addTaskSession,
    onSuccess: () => {
      setIsRunning(true)
      refetch()
      queryClient.invalidateQueries({ queryKey: ["grouped-tasks"] })
    },
    onError: () => {
      setIsRunning(false)
      setElapsedTime(0)
      toast({
        title: translate("Error"),
        description: translate("Unable to start session"),
        variant: "error",
      })
    },
  })

  const finishSessionMutation = useMutation({
    mutationKey: ["finish-task-session-navbar"],
    mutationFn: finishTaskSession,
    onSuccess: () => {
      setIsRunning(false)
      setElapsedTime(0)
      refetch()
      queryClient.invalidateQueries({ queryKey: ["grouped-tasks"] })
    },
    onError: () => {
      toast({
        title: translate("Error"),
        description: translate("Unable to stop session"),
        variant: "error",
      })
    },
  })

  const isLoading = addSessionMutation.isPending || finishSessionMutation.isPending

  const handleToggle = () => {
    if (isLoading) return

    if (isRunning && activeSession?.id) {
      finishSessionMutation.mutate({ sessionId: activeSession.id, userId })
    } else if (!isRunning && taskId) {
      setIsRunning(true)
      addSessionMutation.mutate({ task_id: taskId, active: true, start: Date.now() })
    }
  }

  return (
    <div className='space-y-3 border-t border-gray-200 p-4'>
      <button
        className='flex w-full items-center justify-between gap-3 rounded-full bg-white px-4 py-3 font-medium text-indigo-950 transition-colors'
        onClick={handleToggle}
        disabled={isLoading}
      >
        <div className='pl-3 text-left'>
          <p className='font-[inter] text-base font-semibold text-indigo-900'>
            {data?._projects?.title || "---"}
          </p>
          <p className='font-[inter] text-[10px] font-normal text-indigo-900'>
            {showTimerInFormat(elapsedTime)}
          </p>
        </div>
        <div className='flex h-10 w-10 items-center justify-center rounded-full bg-[radial-gradient(#9B8AFB,#7F6AFF)]'>
          {isRunning ? (
            <Stop color='white' size='18' className='fill-white' />
          ) : (
            <Play color='white' size='18' className='fill-white' />
          )}
        </div>
      </button>

      <div className='space-y-1'>
        {isAdmin && (
          <button className='flex w-full items-center gap-3 rounded-lg bg-neutral-200 px-3 py-2 font-[inter] text-sm font-normal text-[#25272D]'>
            <UserAdd size='16' />
            {translate("Invite teammates")}
          </button>
        )}
        <button className='flex w-full items-center gap-3 rounded-lg bg-neutral-200 px-3 py-2 font-[inter] text-sm font-normal text-[#25272D]'>
          <HelpCircle className='h-4 w-4' />
          {translate("Help docs")}
        </button>
      </div>
    </div>
  )
}

export default memo(NavbarBottom)
